import type { Components, Theme } from '@mui/material';
import { backdropClasses, badgeClasses, outlinedInputClasses } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-pro';
// noinspection ES6UnusedImports
import '@mui/x-data-grid-pro/themeAugmentation';

interface MyThemeProps {
  theme: Theme;
}

const componentOverrides = {
  MuiAccordion: {
    defaultProps: {
      elevation: 3
    },
    styleOverrides: {
      root: {
        '&::before': {
          display: 'none'
        }
      }
    }
  },
  MuiAlert: {
    variants: [
      { props: { severity: 'success' }, style: ({ theme }: MyThemeProps) => ({ color: theme.palette.success.main }) },
      { props: { severity: 'warning' }, style: ({ theme }: MyThemeProps) => ({ color: theme.palette.warning.main }) },
      { props: { severity: 'error' }, style: ({ theme }: MyThemeProps) => ({ color: theme.palette.error.main }) },
      { props: { severity: 'info' }, style: ({ theme }: MyThemeProps) => ({ color: theme.palette.info.main }) }
    ]
  },
  MuiBadge: {
    defaultProps: {
      color: 'primary'
    },
    styleOverrides: {
      badge: () => ({
        padding: '0 5px',
        height: '16px'
      })
    },
    variants: [
      {
        props: { color: 'grey' },
        style: ({ theme }: MyThemeProps) => ({
          [`& .${badgeClasses.badge}`]: { backgroundColor: theme.palette.grey[200] }
        })
      }
    ]
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true
    }
  },
  MuiCard: {
    // Cards read as flat surfaces with a divider-coloured border rather than floating on a shadow.
    // Set as a defaultProp rather than a styleOverride so a call site can still opt back into
    // variant='elevation' where depth is genuinely meaningful.
    defaultProps: {
      variant: 'outlined'
    }
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        border: 'none',
        [`& .${gridClasses.columnSeparator}`]: {
          [`&:not(.${gridClasses['columnSeparator--resizable']})`]: {
            display: 'none'
          }
        }
      }
    }
  },
  MuiDialog: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        [`& .${backdropClasses.root}`]: {
          backgroundColor: theme.palette.colors.steel['A021']
        }
      }),
      paper: ({ theme }: { theme: Theme }) => ({
        boxShadow: theme.shadows[24],
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.common.white,

        minWidth: 400
      })
    }
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',

        padding: theme.spacing(2, 2, 1, 3),
        gap: theme.spacing(1)
      })
    }
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        padding: theme.spacing(1, 2, 2)
      })
    }
  },
  MuiFormControl: {
    styleOverrides: {
      // An outlined field's shrunk label sits at translate(14px, -9px) -- 9px above its own
      // field's top border, by design (that's what draws the notch). Nothing about that
      // clearance is contributed by the field itself; it depends entirely on whatever
      // precedes it. When this FormControl is the very first thing in its container --
      // no heading, no prior sibling, just straight into a dialog or panel -- there's
      // nothing providing that 9px and the label ends up straddling whatever's directly
      // above it (a DialogTitle, the container's own edge) instead of the border.
      // Force it here as a first-of-type safety net so new call sites don't have to
      // remember it. This can't reach every case (a component before the first field,
      // e.g. a heading, still needs >= 16px of its own spacing to the field after it --
      // margins on the same element can't be made to combine via CSS alone), but it
      // covers the common "field is the first thing in a dialog/panel" shape outright.
      // Scoped to FormControls that actually contain a floating label -- a label-less
      // field (a placeholder-only search box, a filled Autocomplete slot) has nothing
      // that pokes above its own box, so it doesn't need the extra room.
      // `:first-of-type` rather than `:first-child` — Emotion's injected style tags break
      // the latter selector and warn in the console.
      // Autocomplete wraps its FormControl in its own root, where it's always first-of-type,
      // so the rule moves up to MuiAutocomplete's root instead (see below).
      root: {
        '&:first-of-type:has(.MuiInputLabel-root):not(.MuiAutocomplete-root *)': {
          marginTop: '9px'
        }
      }
    }
  },
  MuiAutocomplete: {
    styleOverrides: {
      // Same first-field label clearance as MuiFormControl above, applied at the wrapper level.
      root: {
        '&:first-of-type:has(.MuiInputLabel-root)': {
          marginTop: '9px'
        }
      }
    }
  },
  MuiIconButton: {
    variants: [
      { props: { color: 'default' }, style: ({ theme }: MyThemeProps) => ({ color: theme.palette.text.secondary }) },

      { props: { size: 'tiny' }, style: { height: '16px', width: '16px' } },
      { props: { size: 'small' }, style: { height: '24px', width: '24px' } },
      { props: { size: 'medium' }, style: { height: '32px', width: '32px' } },
      { props: { size: 'large' }, style: { height: '40px', width: '40px' } }
    ]
  },
  MuiListItemIcon: {
    // This is an approved override, we will keep these minimal
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        minWidth: theme.spacing(3)
      })
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: ({ theme }: MyThemeProps) => ({
        ...theme.typography.body2
      }),
      notchedOutline: ({ theme }: MyThemeProps) => ({
        borderColor: theme.palette.colors.steel['A021']
      }),
      root: () => ({
        [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
          borderWidth: '1px'
        }
      })
    }
  },
  MuiSelect: {
    styleOverrides: {
      select: () => ({
        display: 'flex',
        // Filled's asymmetric padding happened to make stretched icon+text renderValue rows
        // look centred; outlined's symmetric padding doesn't, so this needs to be explicit.
        alignItems: 'center'
      })
    }
  },
  MuiSkeleton: {
    defaultProps: {
      variant: 'rectangular'
    },
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        backgroundColor: theme.palette.action.hover,
        boxShadow: 'none'
      })
    }
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        minHeight: '32px',
        minWidth: '120px'
      }
    }
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined'
    }
  },
  MuiToolbar: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        flexShrink: '1',
        flexDirection: 'row',

        alignItems: 'center',
        justifyContent: 'space-between',

        [theme.breakpoints.up('xs')]: {
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3)
        }
      }),
      dense: ({ theme }: { theme: Theme }) => ({
        [theme.breakpoints.up('xs')]: {
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2)
        }
      })
    }
  },
  MuiTooltip: {
    defaultProps: {
      arrow: false
    },
    styleOverrides: {
      popper: {
        margin: '0'
      },
      tooltip: ({ theme }: { theme: Theme }) => ({
        color: theme.palette.common.white,
        backgroundColor: theme.palette.grey[700],

        boxShadow: theme.shadows[6],
        borderRadius: '2px',
        padding: '3px 8px'
      })
    }
  }
} as Components;

export default componentOverrides;
